import {
  ConditionGroup,
  ConditionReplicateCell,
  getMethodIdsImplementingConditionGroup,
} from 'studies/typed/shared/useExperimentDesignState';

/** A rendered flowchart cell (one PMEI) within a condition group's replicate row. */
export interface ConditionGroupGridCell {
  methodId: string;
  isArchived: boolean;
  archiveReason?: string;
}

/** A replicate row (a "condition replicate flow") within a condition group's flowchart block. */
export interface ConditionGroupGridRow {
  replicateNumber: number;
  cells: Array<ConditionGroupGridCell>;
}

/** A grid row tagged with the `conditionGroupIndex` and `replicateIndex` its node ids are built from. */
export interface RenderedGridRow {
  conditionGroupIndex: number;
  replicateIndex: number;
  replicateNumber: number;
  cells: Array<ConditionGroupGridCell>;
}

/** Coverage set for a group, falling back to every method when coverage is unspecified. */
function resolveCoveredMethodIds(
  conditionGroup: ConditionGroup,
  methods: ReadonlyArray<{id: string}>
): ReadonlySet<string> {
  return (
    getMethodIdsImplementingConditionGroup(conditionGroup) ?? new Set(methods.map((method) => method.id))
  );
}

/** A live cell for each covered method. */
function makeLiveDraftCells(
  coveredMethodIds: ReadonlySet<string>,
  methods: ReadonlyArray<{id: string}>
): Array<ConditionGroupGridCell> {
  return methods
    .filter((method) => coveredMethodIds.has(method.id))
    .map((method) => ({methodId: method.id, isArchived: false}));
}

/** Whether the group has persisted replicate records (false for drafts). */
function hasPersistedReplicates(conditionGroup: ConditionGroup): boolean {
  const {replicatesByConditionId} = conditionGroup;
  return replicatesByConditionId != null && Object.keys(replicatesByConditionId).length > 0;
}

/** Draft grid: rows `1..numReplicates`, every covered method live. */
function buildDraftGridRows(
  numReplicates: number,
  coveredMethodIds: ReadonlySet<string>,
  methods: ReadonlyArray<{id: string}>
): Array<ConditionGroupGridRow> {
  const rows: Array<ConditionGroupGridRow> = [];
  for (let replicateNumber = 1; replicateNumber <= numReplicates; replicateNumber++) {
    const cells = makeLiveDraftCells(coveredMethodIds, methods);
    if (cells.length > 0) {
      rows.push({replicateNumber, cells});
    }
  }
  return rows;
}

interface PersistedReplicateSummary {
  /** All persisted replicate numbers, ascending. */
  sortedReplicateNumbers: Array<number>;
  /** Number of replicate numbers with a live cell. */
  liveReplicateCount: number;
  /** Highest persisted replicate number, or 0 when there are none. */
  maxReplicateNumber: number;
}

/** Rolls up a group's persisted replicate records into the numbers the grid enumerates over. */
function summarizePersistedReplicates(
  replicatesByConditionId: Record<string, Array<ConditionReplicateCell>>
): PersistedReplicateSummary {
  const persistedNumbers = new Set<number>();
  const liveNumbers = new Set<number>();
  for (const replicates of Object.values(replicatesByConditionId)) {
    for (const replicate of replicates) {
      persistedNumbers.add(replicate.replicateNumber);
      if (!replicate.archived) {
        liveNumbers.add(replicate.replicateNumber);
      }
    }
  }
  const sortedReplicateNumbers = [...persistedNumbers].sort((a, b) => a - b);
  return {
    sortedReplicateNumbers,
    liveReplicateCount: liveNumbers.size,
    maxReplicateNumber: sortedReplicateNumbers[sortedReplicateNumbers.length - 1] ?? 0,
  };
}

/** Replicate numbers for unsaved live replicates, numbered after the highest persisted one. */
function computeAddedReplicateNumbers(
  numReplicates: number,
  liveReplicateCount: number,
  maxReplicateNumber: number
): Array<number> {
  const addedCount = Math.max(0, numReplicates - liveReplicateCount);
  return Array.from({length: addedCount}, (_, index) => maxReplicateNumber + 1 + index);
}

/**
 * Cells for one persisted replicate row, in column order. A covered method with no persisted
 * condition yields a live cell. A grid gap or a hidden archived cell is omitted.
 */
function buildPersistedRowCells(
  replicateNumber: number,
  conditionGroup: ConditionGroup,
  coveredMethodIds: ReadonlySet<string>,
  methods: ReadonlyArray<{id: string}>,
  showArchived: boolean
): Array<ConditionGroupGridCell> {
  const {conditionIdsByPmdvId, replicatesByConditionId} = conditionGroup;
  const cells: Array<ConditionGroupGridCell> = [];
  for (const method of methods) {
    if (!coveredMethodIds.has(method.id)) {
      continue;
    }
    const conditionId = conditionIdsByPmdvId[method.id];
    const replicates = conditionId == null ? undefined : replicatesByConditionId?.[conditionId];
    if (replicates == null) {
      cells.push({methodId: method.id, isArchived: false});
      continue;
    }
    const replicate = replicates.find((candidate) => candidate.replicateNumber === replicateNumber);
    if (replicate == null || (replicate.archived && !showArchived)) {
      continue;
    }
    cells.push({
      methodId: method.id,
      isArchived: replicate.archived,
      ...(replicate.archiveReason != null ? {archiveReason: replicate.archiveReason} : {}),
    });
  }
  return cells;
}

/**
 * Builds a condition group's flowchart rows, one per replicate number keyed by backend
 * `replicateNumber`. Drafts render `1..numReplicates`. Persisted groups list their persisted
 * replicate numbers (keeping archived and gapped rows) plus any newly added live replicates.
 *
 * When `showArchived` is false, archived cells are omitted, dropping fully-archived rows and
 * right-truncating partially-archived ones.
 */
export function getConditionGroupGridRows(
  conditionGroup: ConditionGroup,
  methods: ReadonlyArray<{id: string}>,
  showArchived: boolean
): Array<ConditionGroupGridRow> {
  const coveredMethodIds = resolveCoveredMethodIds(conditionGroup, methods);

  if (!hasPersistedReplicates(conditionGroup)) {
    return buildDraftGridRows(conditionGroup.numReplicates, coveredMethodIds, methods);
  }

  // Non-null by hasPersistedReplicates() above.
  const replicatesByConditionId = conditionGroup.replicatesByConditionId!;
  const {sortedReplicateNumbers, liveReplicateCount, maxReplicateNumber} =
    summarizePersistedReplicates(replicatesByConditionId);
  const addedReplicateNumbers = computeAddedReplicateNumbers(
    conditionGroup.numReplicates,
    liveReplicateCount,
    maxReplicateNumber
  );

  const rows: Array<ConditionGroupGridRow> = [];
  for (const replicateNumber of sortedReplicateNumbers) {
    const cells = buildPersistedRowCells(
      replicateNumber,
      conditionGroup,
      coveredMethodIds,
      methods,
      showArchived
    );
    if (cells.length > 0) {
      rows.push({replicateNumber, cells});
    }
  }
  for (const replicateNumber of addedReplicateNumbers) {
    const cells = makeLiveDraftCells(coveredMethodIds, methods);
    if (cells.length > 0) {
      rows.push({replicateNumber, cells});
    }
  }
  return rows;
}

/**
 * Flattens {@link getConditionGroupGridRows} across all condition groups, tagging each row with the
 * indices its node ids are built from. The single entry point for grid, edge, role, and highlight
 * consumers, so they can't drift on row keying.
 */
export function getRenderedGridRows(
  conditionGroups: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<{id: string}>,
  showArchived: boolean
): Array<RenderedGridRow> {
  return conditionGroups.flatMap((conditionGroup, conditionGroupIndex) =>
    getConditionGroupGridRows(conditionGroup, methods, showArchived).map(({replicateNumber, cells}) => ({
      conditionGroupIndex,
      replicateIndex: replicateNumber - 1,
      replicateNumber,
      cells,
    }))
  );
}
